import { useState, useMemo } from 'react';
import { ViewId } from '~/shared/utils';
import { IconWithCaptionButton } from '~/shared/components/ui/Button';
import { mtechnavi } from '~/shared/libs/clientsdk';
import { ProgressBarComponent } from '~/shared/components/ui/ProgressBar';

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface RankConfig {
  rankCode: string;
  maxScore: number;
  minScore: number;
  colorCode: string;
}

interface CompanyConfirmationIntroductionProps {
  viewId: ViewId;
  introduction?: mtechnavi.api.analysis.IIntroduction;
  scores?: mtechnavi.api.analysis.IScore[];
}
export const CompanyConfirmationIntroduction = ({
  // TODO 将来的に辞書対応をするような時に使う可能性あるので
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewId,
  introduction,
  scores,
}: CompanyConfirmationIntroductionProps) => {
  const [readMore, setReadMore] = useState(false);

  const SRankConfig: RankConfig = {
    rankCode: 'S',
    minScore: 4.6,
    maxScore: 5,
    colorCode: '#FFB800',
  };
  const ARankConfig: RankConfig = {
    rankCode: 'A',
    minScore: 3.8,
    maxScore: 4.5,
    colorCode: '#5D66CC',
  };
  const BRankConfig: RankConfig = {
    rankCode: 'B',
    minScore: 2.1,
    maxScore: 3.7,
    colorCode: '#4CAF50',
  };
  const CRankConfig: RankConfig = {
    rankCode: 'C',
    minScore: 0,
    maxScore: 2.0,
    colorCode: '#9E9E9E',
  };

  const RankConfigs: RankConfig[] = [
    SRankConfig,
    ARankConfig,
    BRankConfig,
    CRankConfig,
  ];

  const [radarLabels, radarScores, radarAvgScores] = useMemo<
    [string[], number[], number[]]
  >(() => {
    const radarLabel: string[] = [];
    const radarScore: number[] = [];
    const radarAvgScore: number[] = [];

    (scores ?? []).map((v) => {
      radarLabel.push(v.name ?? '');
      radarScore.push(v.score ?? 0);
      radarAvgScore.push((v.score ?? 0) - 1);
    });
    return [radarLabel, radarScore, radarAvgScore];
  }, [scores]);

  const radarData = {
    // labels: [
    //   '技術合致度',
    //   '品質・認証',
    //   '納期・機動力',
    //   'コスト',
    //   '経営安定性',
    //   '実績・評価',
    // ],
    // TODO APIの戻り値次第で変更
    labels: radarLabels,
    datasets: [
      {
        label: 'この企業',
        // data: [5, 5, 4, 2.5, 4, 4.8],
        // TODO APIの戻り値次第で変更
        data: radarScores,
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(81, 163, 251, 1)',
        borderWidth: 1,
      },
      {
        label: '企業平均',
        // data: [3, 3, 2, 2.5, 2, 3.8],
        // TODO APIの戻り値次第で変更
        data: radarAvgScores,
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(169, 213, 219, 1)',
        borderWidth: 1,
      },
    ],
  };

  // TODO ランク計算をフロントでする場合を想定して作ったのでBEで行うなら削除する
  // const getRankConfig = (score: number): RankConfig => {
  //   const configs = RankConfigs.filter(
  //     (config) => config.minScore <= score && config.maxScore >= score
  //   );
  //   return configs.length > 0 ? configs[0] : RankConfigs[3];
  // };

  return (
    <>
      公開日：{introduction?.publishedDate || ''}
      <div className="expandable-container-area">
        <div className="container-title">企業紹介</div>
        <div
          className={`expandable-container-text ${
            readMore ? 'is-expanded' : ''
          }`}
        >
          {introduction?.companyOverview || ''}
        </div>
        {introduction?.companyOverview && (
          <IconWithCaptionButton
            name="readMore"
            iconType={readMore ? 'up' : 'down'}
            caption={readMore ? '詳細を閉じる' : 'もっと見る'}
            onClick={() => {
              readMore ? setReadMore(false) : setReadMore(true);
            }}
          />
        )}
      </div>
      <div className="description-container-area">
        <div className="description-container">
          <div className="container-title">得意な生産形態</div>
          <div className="container-text">
            {introduction?.evaluationIndex1 || ''}
          </div>
        </div>
        <div className="description-container">
          <div className="container-title">品質保証・検査</div>
          <div className="container-text">
            {introduction?.evaluationIndex2 || ''}
          </div>
        </div>
      </div>
      <div className="description-container-area">
        <div className="description-container">
          <div className="container-title">主要取引業界</div>
          <div className="container-text">
            {introduction?.evaluationIndex3 || ''}
          </div>
        </div>
        <div className="description-container">
          <div className="container-title">技術・設備のポイント</div>
          <div className="container-text">
            {introduction?.evaluationIndex4 || ''}
          </div>
        </div>
      </div>
      <div className="input-line">
        <div className="item-group-100">
          <div className="w-50">
            <Radar data={radarData} />
          </div>
          <div className="w-50">
            {(scores ?? []).map((score, index) => {
              const scoreVal = score?.score || 0;
              const progressScore = score?.score ? score?.score * 20 : 0;
              const config = RankConfigs.filter(
                (v) => v.rankCode === score.rank
              );
              const colorCode =
                config.length > 0 ? config[0].colorCode : CRankConfig.colorCode;
              return (
                <ProgressBarComponent
                  title={score.name ?? ''}
                  ratingText={`${score.rank ?? ''}ランク (${scoreVal}/5.0)`}
                  progressValue={progressScore}
                  descriptionText={score.remark ?? ''}
                  progressColor={colorCode}
                  key={`bar-${index}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
